import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { ProtectedResource, ResourceAction } from 'types/action.enum';
import { UserRole } from 'types/models/membership';

import {
  makeSelectMyUserHasInternalPermission,
  makeSelectUserRoleGranted,
  selectMyUser,
  selectMyUserInternalAdminCountryAccess,
  selectMyUserIsInternalAdmin,
  selectMyUserMembershipRole
} from './selectors';
import { InternalAdminCountryAccess } from './types';

export function useMyUser() {
  return useSelector(selectMyUser, shallowEqual);
}

export function useMyUserIsInternalAdmin() {
  const isInternalAdmin: boolean = useSelector(selectMyUserIsInternalAdmin, shallowEqual);
  return isInternalAdmin;
}

/**
 * Checks if the user is an enterprise admin.
 * Does not grant roles implicitly to internal admins.
 */
export function useIsEnterpriseAdmin(): boolean {
  const selector = useMemo(() => makeSelectUserRoleGranted([UserRole.ADMIN]), []);
  const userRoleGranted = useSelector(selector, shallowEqual);
  return userRoleGranted;
}

/**
 * Checks if the user is an enterprise admin or supervisor.
 * Does not grant roles implicitly to internal admins.
 */
export function useIsEnterpriseAdminOrSupervisor(): boolean {
  const selector = useMemo(() => makeSelectUserRoleGranted([UserRole.ADMIN, UserRole.SUPERVISOR]), []);
  const userRoleGranted = useSelector(selector, shallowEqual);
  return userRoleGranted;
}

export function useIsEnterpriseMember(): boolean {
  return !!useSelector(selectMyUserMembershipRole, shallowEqual);
}

export function useMyUserInternalAdminCountryAccess(): InternalAdminCountryAccess {
  return useSelector(selectMyUserInternalAdminCountryAccess, shallowEqual);
}

export function useMyUserHasInternalPermission({ resource, action }: {
  resource: ProtectedResource,
  action: ResourceAction
}): boolean {
  const selector = makeSelectMyUserHasInternalPermission({ resource, action });
  const hasPermission: boolean = useSelector(selector, shallowEqual);
  return hasPermission;
}